import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

export default function GeneratorHeader({
  steps,
  activeStep,
  canClickStep,
  isStepComplete,
  onStepClick,
  onOpenMenu,
  onOpenSidebar,
  isSaving,
  lastSavedAt,
  formatTimeHHMM,
  onOpenDesignEditor,
  NumberStepIcon,
  accentColor,
}) {
  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: "1px solid rgba(2,6,23,0.08)",
        backdropFilter: "blur(10px)",
        background:
          "linear-gradient(90deg, rgba(125,156,20,0.12), rgba(34,211,238,0.10))",
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          px: 1,
          minHeight: 52,
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <IconButton size="small" onClick={onOpenSidebar} aria-label="Kapitel öffnen">
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.015em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: { xs: 200, sm: 360, md: 520 },
            }}
            title={steps[activeStep]?.title}
          >
            {steps[activeStep]?.title}
          </Typography>
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            pointerEvents: "none",
            display: { xs: "none", md: "none", lg: "flex" },
          }}
        >
          {steps.map((_, idx) => {
            const active = idx === activeStep;
            const complete = isStepComplete(idx);
            const clickable = canClickStep(idx);

            return (
              <Box key={idx} sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip
                  title={steps[idx]?.title}
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: 13,
                        fontWeight: 600,
                        px: 1.25,
                        py: 0.6,
                        maxWidth: 260,
                      },
                    },
                  }}
                >
                  <IconButton
                    onClick={() => clickable && onStepClick(idx)}
                    disabled={!clickable}
                    size="small"
                    sx={{ p: 0.5, overflow: "visible", pointerEvents: "auto" }}
                    aria-label={`Schritt ${idx + 1}`}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "999px",
                        bgcolor: complete ? "#10b981" : active ? accentColor : "#cbd5f5",
                        boxShadow: active ? "0 0 0 4px #7d9c14" : "none",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                    </Box>
                  </IconButton>
                </Tooltip>
                {idx < steps.length - 1 && (
                  <Box
                    sx={{
                      width: 20,
                      height: 2,
                      borderRadius: 1,
                      bgcolor: "#d0d0d0",
                      mx: 0.5,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "inline" } }}>
            {isSaving ? "Speichern…" : lastSavedAt ? `Lokal gespeichert ${formatTimeHHMM(lastSavedAt)}` : "Lokale Daten geladen"}
          </Typography>
          <Tooltip title="Menü">
            <IconButton size="small" onClick={onOpenMenu}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            onClick={onOpenDesignEditor}
            variant="contained"
            size="large"
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            sx={{
              ml: 0.5,
              px: 3,
              py: 1,
              borderRadius: 999,
              fontWeight: 800,
              color: "#14150c",
              whiteSpace: "nowrap",
              background: "linear-gradient(135deg, #c3f04c 0%, #a3d81e 100%)",
              boxShadow: "0 10px 26px rgba(23, 23, 15, 0.25)",
              transition: "transform 180ms ease, box-shadow 180ms ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 14px 32px rgba(23, 23, 15, 0.35)",
                background: "linear-gradient(135deg, #b5e63e 0%, #94c716 100%)",
              },
            }}
          >
            Design Editor
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
